// The partner-brand wizard as a POPUP ON THE RECORD — the create/edit surface
// for a broker's or brokerage's Prequalification tab. Launching the wizard used
// to navigate to /partners/brands/new and land on the brand detail page
// afterwards, so the staff member never got back to the record they started
// from. This opens in place instead; `onCompleted` is how the host closes and
// refreshes.
//
// It renders PartnerBrandWizardBody — the SAME component the route renders.
// Only the chrome differs.
//
// Warn before closing. Nothing RESUMES — reopening always starts at step 1, so
// discard means discard — but it is not true that nothing is persisted: step 1
// writes a REAL brand row inactive, so closing after it strands an unreachable
// site in the Partner Brands list rather than losing everything. Which of those
// two an abandoned popup is actually in is what the guard copy below reports.
// The shared Modal has no blocked-close path, but all three of its close routes
// (Escape, backdrop, the X button) funnel through the single `onClose` prop, so
// guarding at the caller is complete rather than a compromise — no change to
// Modal, which has 230 importers. Backdrop dismissal is off outright, following
// the two existing multi-step wizard precedents (RecordPaymentWizard,
// NewDealWizard): a five-step form should not be dismissible by a stray click at
// all.
//
// The guard covers IN-FLIGHT work as well as entered values. Reading a brand
// guide takes up to a minute and dirties nothing while it runs, so a guard on
// `dirty` alone let a staff member thirty seconds into that call close the
// wizard instantly and silently. `busy` is reported by the body for exactly this
// reason, and the confirm says which of the two is at stake: telling someone
// they will lose values they never typed is as unhelpful as saying nothing.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  PartnerBrandWizardBody,
  type PartnerBrandWizardState,
} from "./partnerBrandForm/PartnerBrandWizardBody";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  /** Create: pre-set the owning broker (still changeable inside). */
  ownerBrokerId?: string;
  ownerBrokerName?: string;
  /** Create: pre-set the owning brokerage (still changeable inside). */
  ownerBrokerageId?: string;
  ownerBrokerageName?: string;
  /** Edit: the brand to edit. */
  brandId?: string;
  /** Went live (create) or saved (edit) — close and refresh. */
  onCompleted: () => void;
}

const CLEAN: PartnerBrandWizardState = {
  dirty: false,
  draftBrandId: null,
  busy: false,
};

/** Title, buttons and body for the close confirm, chosen by what closing would
 *  actually cost. In-flight work wins over unsaved values: it is the more
 *  urgent fact, and it is the one the staff member cannot see for themselves. */
function closeGuardCopy(state: PartnerBrandWizardState) {
  if (state.busy) {
    return {
      title: "Close while this is still running?",
      confirmLabel: "Close anyway",
      cancelLabel: "Keep waiting",
      // Deliberately not "cancels it" — closing tears down the wizard, it does
      // not abort the request. Saying otherwise would be a lie about a save.
      message: (
        <>
          Something is still running — the brand guide is being read, or a step is
          saving. Closing will not stop the request, it just ends your view of it:
          anything read from the guide is <strong>lost</strong>, and a save already
          sent may still land.
        </>
      ),
    };
  }
  return {
    title: "Discard this site?",
    confirmLabel: "Discard",
    cancelLabel: "Keep editing",
    message: state.draftBrandId ? (
      <>
        What you have entered since the last step is lost. The site was already saved
        as an <strong>inactive draft</strong> — it is not live and nobody can reach it,
        but it stays in the Partner Brands list until someone finishes or deletes it.
      </>
    ) : (
      <>
        What you have entered is lost — this wizard keeps no draft you can come back
        to.
      </>
    ),
  };
}

export function PartnerBrandWizardModal({
  open,
  onOpenChange,
  mode,
  ownerBrokerId,
  ownerBrokerName,
  ownerBrokerageId,
  ownerBrokerageName,
  brandId,
  onCompleted,
}: Props) {
  const [state, setState] = useState<PartnerBrandWizardState>(CLEAN);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const close = () => {
    setConfirmOpen(false);
    setState(CLEAN);
    onOpenChange(false);
  };

  const requestClose = () =>
    state.dirty || state.busy ? setConfirmOpen(true) : close();

  const isEdit = mode === "edit";
  const guard = closeGuardCopy(state);

  return (
    <Modal
      open={open}
      onClose={requestClose}
      dismissOnBackdropClick={false}
      size="lg"
      title={isEdit ? "Edit prequalification site" : "New prequalification site"}
      description={
        isEdit
          ? "White-label skin for the public prequalification funnel."
          : "White-label skin for the public prequalification funnel. It stays inactive until you go live on the last step."
      }
    >
      {/* Mounted only while open, so reopening always starts clean. */}
      {open && (
        <PartnerBrandWizardBody
          mode={mode}
          brandId={brandId}
          ownerBrokerId={ownerBrokerId}
          ownerBrokerName={ownerBrokerName}
          ownerBrokerageId={ownerBrokerageId}
          ownerBrokerageName={ownerBrokerageName}
          onCancel={requestClose}
          onStateChange={setState}
          onCompleted={() => {
            setState(CLEAN);
            setConfirmOpen(false);
            onCompleted();
            onOpenChange(false);
          }}
        />
      )}

      {/* Nested modal — the RecordPaymentWizard precedent. */}
      <ConfirmModal
        open={confirmOpen}
        title={guard.title}
        destructive
        confirmLabel={guard.confirmLabel}
        cancelLabel={guard.cancelLabel}
        message={guard.message}
        onConfirm={close}
        onCancel={() => setConfirmOpen(false)}
      />
    </Modal>
  );
}
